import React, { useRef, useEffect, useState } from "react";
import useHttp from "../../../../hooks/use-http";
import moment from "moment";
import Pagination from "../../../../components/Pagination";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { AutoComplete } from "antd";
import { toast } from "react-toastify";
import convertToExcel from "../../../../components/ExportToExcel";

const FarmerScoreboardReport = () => {
  const formatNumber = (num) => {
    if (num === null || num === undefined) return "0.00";
    return Number(num).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const frmRef = useRef({});
  const navigate = useNavigate();

  const handleButtonClick = () => {
    navigate("/farmerwise");
  };

  const [employeeId, setEmployeeId] = useState();
  const [employeeData, setEmployeeData] = useState([]);
  const [seasonlist, setSeasonlist] = useState(null);
  const [seasonId, setSeasonId] = useState();
  const [ranchId, setRanchId] = useState();
  const [ranchData, setRanchData] = useState([]);
  const [cropId, setCropId] = useState("");
  const [cropData, setCropData] = useState([]);
  const [fieldName, setFieldName] = useState("");
  const [fieldOptions, setFieldOptions] = useState([]);
  const [farmerNumber, setFarmerNumber] = useState("");
  const [farmerData, setFarmerData] = useState();
  const [data, setData] = useState([]);
  const [employeeAllData, setEmployeeAllData] = useState([]);
  const itemsPerPage = localStorage.getItem("pageSize") || 10;
  const [currentPage, setCurrentPage] = useState(1);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = data && data.slice(indexOfFirstItem, indexOfLastItem);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const [expandedGroups, setExpandedGroups] = useState({});
  useEffect(() => {
    const initialState = {};
    Object.keys(activityStructure).forEach((heading) => {
      initialState[heading] = true;
    });
    setExpandedGroups(initialState);
  }, [data]);
  const toggleGroup = (heading) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [heading]: !prev[heading],
    }));
  };

  const { sendRequest: getprocurementData } = useHttp();

  const fillprocurementData = (e) => {
    if (e) e.preventDefault();
    let urlfilter = "";

    if (employeeId > 0) urlfilter += `&fieldExecutiveId=${employeeId}`;
    if (seasonId > 0) urlfilter += `&seasonId=${seasonId}`;
    if (ranchId > 0) urlfilter += `&ranchId=${ranchId}`;
    if (fieldName && fieldName !== "") {
      urlfilter += `&fieldId=${fieldName}`;
    }
    if (farmerNumber) urlfilter += `&farmerNo=${farmerNumber}`;
    if (cropId) urlfilter += `&cropId=${cropId}`;

    const url = `/api/farmerScoreboardReport/?orgId=${localStorage.getItem(
      "orgId"
    )}&empId=${localStorage.getItem("empId")}${urlfilter}`;

    getprocurementData({ url }, (data) => {
      setData(data);
    });
  };

  const farmerDetailHeaders = [
    "Farmer No",
    "Farmer Name",
    "Season Name",
    "Crop",
    "Field Name",
    "Plot Area",
    "Plot Size Unit",
    "Extension Officer",
  ];

  const farmerDetailFields = [
    "farmerNo",
    "farmerName",
    "seasonName",
    "cropName",
    "plotCode",
    "plotArea",
    "plotAreaUnit",
    "executiveName",
  ];

  // -- Export Handler -----------------------------------
  const handleExportExcel = () => {
    if (!data || data.length === 0) {
      toast.error("No data available to export!");
      return;
    }
    const filename = "FarmerScoreboardReport.xlsx";

    // --------- Build dynamic header arrays -------------
    const sectionHeaderRow = [
      ...farmerDetailHeaders.map((_, i) => (i === 0 ? "Farmer Details" : "")),
      ...Object.entries(activityStructure).flatMap(([heading, subs]) =>
        subs.length > 0 ? [heading, ...Array(subs.length - 1).fill("")] : []
      ),
      "Total Score",
      "Percentage",
    ];

    const subHeaderRow = [
      ...farmerDetailHeaders,
      ...Object.entries(activityStructure).flatMap(([heading, subs]) => subs),
      "Total Score",
      "Percentage (%)",
    ];

    // ------------- Build worksheet rows ----------------
    const exportRows = data.map((itm) => {
      // Farmer details
      const base = farmerDetailFields.map((f) => itm[f] ?? "-");

      // Dynamic activities
      const dyn = [];
      Object.entries(activityStructure).forEach(([heading, subs]) => {
        let ratings = [];
        // special logic: "Planting" column should find from "Farm Management" activities
        if (heading === "Planting") {
          const farmAct = itm.activities?.find(
            (a) => a.mainHeading === "Farm Management"
          );
          ratings = farmAct?.subHeadings || [];
          subs.forEach((subLabel, idx) => {
            const plantingMatch = ratings.find(
              (r) => (r.activity || "").trim().toLowerCase() === "planting"
            );
            dyn.push(plantingMatch?.rating ?? "-");
          });
        } else {
          const act = itm.activities?.find((a) => a.mainHeading === heading);
          ratings = act?.subHeadings || [];
          subs.forEach((subLabel, idx) => {
            let rating = "-";
            if (heading === "Farm Management") {
              const match = ratings.find(
                (r) =>
                  (r.activity || "").trim().toLowerCase() ===
                  (subLabel || "").trim().toLowerCase()
              );
              rating = match?.rating ?? "-";
            } else {
              rating = ratings[idx]?.rating ?? "-";
            }
            dyn.push(rating);
          });
        }
      });

      // Score and percentage
      return [...base, ...dyn, itm.totalScore ?? "-", itm.percentage ?? "-"];
    });

    // ---------- Calculate header merges dynamically -----
    let merges = [];
    // Farmer Details (A1:H1) if 8 columns
    if (farmerDetailHeaders.length > 1)
      merges.push({
        s: { r: 0, c: 0 },
        e: { r: 0, c: farmerDetailHeaders.length - 1 },
      });
    let colOffset = farmerDetailHeaders.length;
    Object.entries(activityStructure).forEach(([heading, subs]) => {
      if (subs.length === 1) {
        merges.push({ s: { r: 0, c: colOffset }, e: { r: 1, c: colOffset } });
        colOffset += 1;
      } else if (subs.length > 1) {
        merges.push({
          s: { r: 0, c: colOffset },
          e: { r: 0, c: colOffset + subs.length - 1 },
        });
        colOffset += subs.length;
      }
    });
    // Total Score & Percentage columns
    merges.push({ s: { r: 0, c: colOffset }, e: { r: 1, c: colOffset } });
    merges.push({
      s: { r: 0, c: colOffset + 1 },
      e: { r: 1, c: colOffset + 1 },
    });

    // ------------ Compose worksheet & export ------------
    const ws_data = [sectionHeaderRow, subHeaderRow, ...exportRows];

    const ws = XLSX.utils.aoa_to_sheet(ws_data);
    ws["!merges"] = merges;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Scoreboard");
    XLSX.writeFile(wb, filename);
  };

  const { sendRequest: getEmp } = useHttp();

  const EmployeeDetail = (region) => {
    var urlfilter = "";

    if (region > 0) {
      urlfilter = urlfilter + `&regionId=${region}`;
    }
    getEmp(
      {
        url: `/api/fillEmployeeForReortwithCase/?useCase=Procurement&empId=${localStorage.getItem(
          "empId"
        )}&orgId=${localStorage.getItem("orgId")}${urlfilter} `,
      },
      (data) => {
        // console.log("emp", data);
        var emplist = data;

        setEmployeeData(emplist);
        setEmployeeAllData(emplist);
      }
    );
  };

  useEffect(() => {
    EmployeeDetail(0);
  }, [getEmp]);

  const { sendRequest: getSeason } = useHttp();
  useEffect(() => {
    getSeason(
      {
        url: `/api/seasons/?orgId=${localStorage.getItem("orgId")}`,
      },
      (data) => {
        setSeasonlist(data);
      }
    );
  }, [getSeason]);

  const { sendRequest: getCropData } = useHttp();

  useEffect(() => {
    getCropData(
      {
        url: `/api/farmerCropList/?orgId=${localStorage.getItem("orgId")}`,
      },
      (data) => {
        setCropData(data.output || []);
      }
    );
  }, [getCropData]);

  let activityStructure = {};

  // Build the structure
  data?.forEach((item) => {
    item.activities?.forEach((act) => {
      let key = act.mainHeading;

      act.subHeadings.forEach((sub, idx) => {
        let label =
          act.mainHeading === "Farm Management"
            ? sub.activity
            : `${key} ${idx + 1}`;

        // ✅ Move Planting out of Farm Management
        if (act.mainHeading === "Farm Management" && label === "Planting") {
          if (!activityStructure["Planting"]) {
            activityStructure["Planting"] = [];
          }
          if (!activityStructure["Planting"].includes("Planting")) {
            activityStructure["Planting"].push("Planting");
          }
        } else {
          if (!activityStructure[key]) {
            activityStructure[key] = [];
          }
          if (!activityStructure[key].includes(label)) {
            activityStructure[key].push(label);
          }
        }
      });
    });
  });

  // ✅ Force EXACT column sequence
  const desiredOrder = [
    "Planting",
    "Fertilizer",
    "Weeding",
    "Spraying",
    "Farm Management",
  ];

  // Rebuild in exact order (ignore anything else not in list)
  activityStructure = desiredOrder.reduce((acc, key) => {
    if (activityStructure[key]) {
      acc[key] = activityStructure[key];
    }
    return acc;
  }, {});

  // ✅ Reorder so Planting comes first
  if (activityStructure["Planting"]) {
    const { Planting, ...rest } = activityStructure;
    activityStructure = { Planting, ...rest };
  }

  const [expandedHeadings, setExpandedHeadings] = useState({});
  useEffect(() => {
    const initialExpanded = {};
    Object.keys(activityStructure).forEach((key) => {
      initialExpanded[key] = true; // set to true for default expanded
    });
    setExpandedHeadings(initialExpanded);
  }, [data]);

  const toggleHeading = (heading) => {
    setExpandedHeadings((prev) => ({
      ...prev,
      [heading]: !prev[heading],
    }));
  };

  const { sendRequest: getRanch } = useHttp();
  useEffect(() => {
    getRanch(
      {
        url: `/api/ranchDetail/?orgId=${localStorage.getItem("orgId")}`,
      },
      (data) => {
        setRanchData(data);
      }
    );
  }, [getRanch]);

  const { sendRequest: getFarmerNumber } = useHttp();
  useEffect(() => {
    var url = "";
    if (employeeId > 0) {
      url = url + '&fieldExecutiveId=' + employeeId;
    }

    if (seasonId > 0) {
      url = url + '&seasonId=' + seasonId;
    }

    if (ranchId > 0) {
      url = url + '&ranchId=' + ranchId;
    }




    getFarmerNumber(
      {
        url: `/api/scordBoardFarmerNumberList/?orgId=${localStorage.getItem(
          "orgId"
        )}&empId=${localStorage.getItem(
          "empId"
        )}${url}`,
      },
      (data) => {
        setFarmerData(data);
      }
    );
  }, [getFarmerNumber, employeeId, ranchId, seasonId]);


  const handleSearch = (value) => {
    setFarmerNumber(value);
    setData([]);
  };

  const filteredOptions = farmerData
    ?.filter((item) => item.farmerNo.includes(farmerNumber))
    .map((item) => ({ value: item.farmerNo }));

  useEffect(() => {
    fillprocurementData();
  }, [
    seasonId,
    employeeId,
    ranchId,
    fieldName,
    farmerNumber,
    cropId,
  ]);

  useEffect(() => {
    if (!data || data.length === 0) {
      setFieldOptions([]);
      return;
    }

    let filteredData = data;

    if (ranchId && ranchId > 0) {
      const selectedRanch = ranchData.find(
        (x) => String(x.ranchId) === String(ranchId)
      );

      if (selectedRanch) {
        filteredData = data.filter(
          (item) => item.ranchName === selectedRanch.ranchName
        );
      }
    }

    const uniqueFields = [
      ...new Set(
        filteredData
          .map((item) => item.plotCode)
          .filter(Boolean)
      ),
    ];

    const fieldList = uniqueFields.map((f) => ({
      plotCode: f,
    }));

    setFieldOptions(fieldList);
  }, [ranchId, ranchData, data]);

  return (
    <div className="content-wrapper">
      <div className="row mx-2">
        <div className="col-md-8 mt-3">
          <label className="card-title mx-3">Farmer Scoreboard Report</label>
        </div>
        <div className="col-md-4 text-right mt-3">
          <button className="btn btn-tool" onClick={handleButtonClick}>
            <i className="fas fa-times" />
          </button>
        </div>
      </div>

      <section className="content">
        <form onSubmit={fillprocurementData}>
          <div className="container-fluid">
            <div className="row">

              {/* Crop Name */}
              <div className="col-md-3 form-group">
                <label>Crop Name</label>
                <select
                  className="form-control"
                  value={cropId}
                  onChange={(e) => setCropId(e.target.value)}
                >
                  <option value="">Select</option>

                  {cropData &&
                    cropData.map((x) => (
                      <option key={x.CropId} value={x.CropId}>
                        {x.CropName}
                      </option>
                    ))}
                </select>
              </div>

              {/* Season */}
              <div className="col-md-3 form-group">
                <label>Season</label>
                <select
                  className="form-control"
                  value={seasonId}
                  onChange={(e) => setSeasonId(e.target.value || 0)}
                >
                  <option value="">Select</option>

                  {seasonlist?.map((x) => (
                    <option key={x.seasonId} value={x.seasonId}>
                      {x.seasonName}
                    </option>
                  ))}
                </select>
              </div>

              {/* Ranch */}
              <div className="col-md-3 form-group">
                <label>Ranch</label>

                <select
                  className="form-control"
                  value={ranchId}
                  onChange={(e) => {
                    setRanchId(e.target.value || 0);
                    setFieldName("");
                  }}
                >
                  <option value="">Select</option>

                  {ranchData?.map((x) => (
                    <option key={x.ranchId} value={x.ranchId}>
                      {x.ranchName}
                    </option>
                  ))}
                </select>
              </div>

              {/* Field Name */}
              <div className="col-md-3 form-group">
                <label>Field Name</label>

                <select
                  className="form-control"
                  value={fieldName}
                  onChange={(e) => {
                    setFieldName(e.target.value);
                  }}
                >
                  <option value="">Select</option>

                  {fieldOptions.map((x, i) => (
                    <option key={i} value={x.plotCode}>
                      {x.plotCode}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Second Row */}
            <div className="row mt-2">

              {/* Farmer Number */}
              <div className="col-md-3 form-group">
                <label>Farmer Number</label>

                <AutoComplete
                  className="form-control"
                  value={farmerNumber}
                  options={filteredOptions}
                  placeholder="Farmer Number"
                  onChange={handleSearch}
                />
              </div>

              {/* Submit Button */}
              <div className="col-md-3 form-group d-flex align-items-end">
                <button
                  type="submit"
                  className="btn btn-primary w-100"
                  style={{ height: "38px" }}
                >
                  Submit
                </button>
              </div>
            </div>
          </div>
        </form>

        <h5>Please select the parameters to get the desired result</h5>
        <hr />
      </section>

      <section className="content">
        <div className="container-fluid">
          <div className="text-right my-2">
            <button
              className="btn btn-sm btn-warning"
              onClick={handleExportExcel}
            >
              <i className="fas fa-file-excel"></i>
            </button>
          </div>

          <div className="card">
            {currentItems.length > 0 ? (
              <>
                <div className="card-body">
                  <table className="table table-bordered table-responsive text-center">
                    <thead>
                      <tr className="tableheaderrowview">
                        <th colSpan={7}>Farmer Details</th>

                        {Object.entries(activityStructure).map(
                          ([heading, subs]) => (
                            <th
                              key={heading}
                              colSpan={
                                expandedGroups[heading] ? subs.length : 1
                              }
                              style={{ cursor: "pointer" }}
                              onClick={() => toggleGroup(heading)}
                            >
                              {heading} {expandedGroups[heading] ? "-" : "+"}
                            </th>
                          )
                        )}

                        <th rowSpan={2}>Total Score</th>
                        <th rowSpan={2}>Percentage</th>
                      </tr>
                      <tr className="table-subheader">
                        <th>Farmer No</th>
                        <th>Farmer Name</th>
                        <th>Season Name</th>
                        <th>Crop</th>
                        <th>Field Name</th>
                        <th>Plot Area </th>
                        <th>Plot Size Unit</th>

                        {Object.entries(activityStructure).flatMap(
                          ([heading, subs]) =>
                            expandedGroups[heading] ? (
                              // Expanded → show all subcolumns
                              subs.map((subLabel, idx) => (
                                <th key={`${heading}_${idx}`}>{subLabel}</th>
                              ))
                            ) : (
                              // Collapsed → show only one column with sum
                              <th key={`${heading}_collapsed`}>
                                {(() => {
                                  const sum = subs.reduce(
                                    (acc, val) => acc + (Number(val) || 0),
                                    0
                                  );
                                  return sum === 0 ? "Total" : sum;
                                })()}
                              </th>
                            )
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {currentItems.map((itm, i) => (
                        <tr key={i}>
                          <td>{itm.farmerNo}</td>
                          <td>{itm.farmerName}</td>
                          <td>{itm.seasonName}</td>
                          <td>{itm.cropName}</td>
                          <td>{itm.plotCode}</td>
                          <td>{formatNumber(itm.plotArea)}</td>
                          <td>{itm.plotSizeUnit}</td>
                          {/* <td>
                                  {(() => {
                                    const farmManagement = itm.activities?.find(
                                      (a) => a.mainHeading === "Farm Management"
                                    );
                                    const planting =
                                      farmManagement?.subHeadings?.find(
                                        (s) => s.activity === "Planting"
                                      );
                                    const value = planting?.rating ?? "-";
                                    return !isNaN(Number(value))
                                      ? formatNumber(value)
                                      : value;
                                  })()}
                                </td> */}
                          {Object.entries(activityStructure).flatMap(
                            ([heading, subs]) => {
                              if (heading === "Planting") {
                                if (expandedGroups[heading]) {
                                  // Expanded: show single planting rating (from Farm Management)
                                  const farmManagement = itm.activities?.find(
                                    (a) => a.mainHeading === "Farm Management"
                                  );
                                  const planting =
                                    farmManagement?.subHeadings?.find(
                                      (s) =>
                                        s.activity?.trim().toLowerCase() ===
                                        "planting"
                                    );
                                  const value = planting?.rating ?? "-";
                                  return <td key={`planting_${i}`}>{value}</td>;
                                } else {
                                  // Collapsed: show sum of all numeric planting ratings (from Farm Management)
                                  const farmManagement = itm.activities?.find(
                                    (a) => a.mainHeading === "Farm Management"
                                  );
                                  const plantings =
                                    farmManagement?.subHeadings?.filter(
                                      (s) =>
                                        s.activity?.trim().toLowerCase() ===
                                        "planting"
                                    ) || [];
                                  let sum = 0;
                                  let hasValid = false;
                                  plantings.forEach((p) => {
                                    const num = Number(p.rating);
                                    if (
                                      !isNaN(num) &&
                                      num !== null &&
                                      num !== undefined &&
                                      p.rating !== "-"
                                    ) {
                                      sum += num;
                                      hasValid = true;
                                    }
                                  });
                                  return (
                                    <td key={`planting_${i}`}>
                                      {hasValid ? formatNumber(sum) : "-"}
                                    </td>
                                  );
                                }
                              }

                              const act = itm.activities?.find(
                                (a) => a.mainHeading === heading
                              );
                              const ratings = act?.subHeadings || [];

                              if (expandedGroups[heading]) {
                                // Expanded: show each subheading value
                                return subs.map((subLabel, idx) => {
                                  // Skip 'Planting' under 'Farm Management'
                                  if (
                                    heading === "Farm Management" &&
                                    subLabel?.trim().toLowerCase() ===
                                    "planting"
                                  ) {
                                    return null;
                                  }

                                  let rating = "-";
                                  if (heading === "Farm Management") {
                                    const match = ratings.find(
                                      (r) =>
                                        r.activity?.trim().toLowerCase() ===
                                        subLabel?.trim().toLowerCase()
                                    );
                                    rating = match?.rating ?? "-";
                                  } else {
                                    rating = ratings[idx]?.rating ?? "-";
                                  }
                                  const isNumeric =
                                    !isNaN(parseFloat(rating)) &&
                                    isFinite(rating);

                                  return (
                                    <td key={`${i}_${heading}_${idx}`}>
                                      {isNumeric
                                        ? formatNumber(rating)
                                        : rating}
                                    </td>
                                  );
                                });
                              } else {
                                // Collapsed: show sum of all valid numeric subheading values
                                let sum = 0;
                                let hasValid = false;
                                ratings.forEach((r) => {
                                  if (
                                    r.rating &&
                                    !(
                                      heading === "Farm Management" &&
                                      r.activity?.trim().toLowerCase() ===
                                      "planting"
                                    )
                                  ) {
                                    const num = Number(r.rating);
                                    if (
                                      !isNaN(num) &&
                                      num !== null &&
                                      num !== undefined &&
                                      r.rating !== "-"
                                    ) {
                                      sum += num;
                                      hasValid = true;
                                    }
                                  }
                                });

                                return (
                                  <td key={`${i}_${heading}_collapsed`}>
                                    {hasValid ? formatNumber(sum) : "-"}
                                  </td>
                                );
                              }
                            }
                          )}
                          <td>{formatNumber(itm.totalScore)}</td>{" "}
                          <td>{formatNumber(itm.percentage)}%</td>{" "}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {/* <table className="table table-bordered table-responsive text-center">
                    <thead>
                      <tr className="tableheaderrowview">
                        <th colSpan={8}>Farmer Details</th>
                        {Object.entries(activityStructure).map(
                          ([heading, subs]) => (
                            <th
                              key={heading}
                              colSpan={
                                expandedHeadings[heading] ? subs.length : 1
                              }
                            >
                              <div
                                onClick={() => toggleHeading(heading)}
                                style={{ cursor: "pointer" }}
                              >
                                {heading}{" "}
                                <span>
                                  {expandedHeadings[heading] ? "–" : "+"}
                                </span>
                              </div>
                            </th>
                          )
                        )}
                        <th rowSpan={2}>Total Score</th>
                        <th rowSpan={2}>Percentage</th>
                      </tr>
                      <tr className="table-subheader">
                        <th>Farmer Id</th>
                        <th>Farmer Name</th>
                        <th>Crop</th>
                        <th>Field Category </th>
                        <th>Field ID</th>
                        <th>Extension Officer</th>
                        <th>Field Size</th>
                        <th>Plot Name</th>

                        <th>Plot Size Unit</th>

                        <th>Season Name</th>

                        {Object.entries(activityStructure).flatMap(
                          ([heading, subs]) =>
                            expandedHeadings[heading]
                              ? subs.map((subLabel, idx) => (
                                  <th key={`${heading}_${idx}`}>{subLabel}</th>
                                ))
                              : [<th key={`${heading}_collapsed`}>Sum</th>]
                        )}
                      </tr>
                    </thead>

                    <tbody>
                      {currentItems.map((itm, i) => (
                        <tr
                          key={i}
                          className={
                            i % 2 === 0 ? "table-row-even" : "table-row-odd"
                          }
                        >
                          <td>{itm.farmerNo}</td>
                          <td>{itm.farmerName}</td>
                          <td>{itm.cropName}</td>
                          <td>{itm.ranchName}</td>
                          <td>{itm.plotCode}</td>
                          <td>{itm.plotAreaUnit}</td>
                          <td>{itm.executiveName}</td>
                          <td>{formatNumber(itm.plotArea)}</td>{" "}
                          <td>{itm.seasonName}</td>
                          {Object.entries(activityStructure).flatMap(
                            ([heading, subs]) => {
                              const act = itm.activities?.find(
                                (a) => a.mainHeading === heading
                              );
                              const ratings = act?.subHeadings || [];

                              if (!expandedHeadings[heading]) {
                                const sum = ratings.reduce(
                                  (total, r) => total + (Number(r.rating) || 0),
                                  0
                                );
                                return (
                                  <td key={`${i}_${heading}_collapsed`}>
                                    {formatNumber(sum)}
                                  </td>
                                );
                              }

                              return subs.map((subLabel, idx) => {
                                let rating = "-";

                                if (heading === "Farm Management") {
                                  const match = ratings.find(
                                    (r) => r.activity === subLabel
                                  );
                                  rating = match?.rating ?? "-";
                                } else {
                                  rating = ratings[idx]?.rating ?? "-";
                                }

                                const isNumeric =
                                  !isNaN(parseFloat(rating)) &&
                                  isFinite(rating);

                                return (
                                  <td key={`${i}_${heading}_${idx}`}>
                                    {isNumeric ? formatNumber(rating) : rating}
                                  </td>
                                );
                              });
                            }
                          )}
                          <td>{formatNumber(itm.totalScore)}</td>{" "}
                          <td>{formatNumber(itm.percentage)}%</td>{" "}
                        </tr>
                      ))}
                    </tbody>
                  </table> */}
                </div>

                <div className="card-footer clearfix">
                  <Pagination
                    totalItems={data.length}
                    itemsPerPage={itemsPerPage}
                    onPageChange={paginate}
                    currentPage={currentPage}
                    setCurrentPage={setCurrentPage}
                  />
                </div>
              </>
            ) : (
              <p className="text-center">No data available.</p>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default FarmerScoreboardReport;
